import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification, Select } from 'antd';
import { ConfirmationModal } from '@crc-evans/ui-components';
import { FileSpreadsheet, Plus, Upload as UploadIcon } from 'lucide-react';
import {
  createVerificationCriterion,
  deleteVerificationCriterion,
  fetchVerificationCriteria,
  importVerificationCriteriaXlsx,
  updateVerificationCriterion,
} from '../../services/arcWelding';
import { useArcWeldingFormOptions } from '../../hooks/useArcWeldingFormOptions';
import { downloadDataAsExcel } from '../../utils/downloadExcelUtils';
import {
  AW_FILTER_LABELS,
  CRITERIA_EXCEL_COLUMNS,
  fileToBase64,
  mapCriteriaRowForExcel,
  normalizePagedResponse,
} from '../../utils/arcWeldingVerificationUi';
import ArcWeldingCriteriaForm from './ArcWeldingCriteriaForm';
import ArcWeldingListShell, {
  AwFilterGroup,
  AwFilterSearch,
  AwFilterSelect,
  buildActionsColumn,
  buildServerPagination,
  buildTextColumn,
  useDebouncedSearch,
} from './ArcWeldingListShell';
import './ArcWelding.css';

const { Option } = Select;

const ArcWeldingCriteriaPage = () => {
  const { options } = useArcWeldingFormOptions();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchInput, debouncedSearch, setSearchInput] = useDebouncedSearch();
  const [parameterFilter, setParameterFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [importing, setImporting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const importInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchVerificationCriteria({
        search: debouncedSearch || undefined,
        parameter: parameterFilter || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        pageNumber,
        pageSize,
      });
      const paged = normalizePagedResponse(payload);
      setItems(paged.items);
      setTotal(paged.total);
    } catch (err) {
      notification.error({
        message: 'Failed to load verification criteria',
        description: err?.message || err?.error || String(err),
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, parameterFilter, isActiveFilter, pageNumber, pageSize]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, parameterFilter, isActiveFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    try {
      // Build a valid .xlsx on the client. Server export
      // (GET …/verification-criteria-export/xlsx) often returns a
      // corrupt OpenXML zip that Excel must "recover".
      const pageSizeAll = 500;
      let page = 1;
      let allRows = [];
      let totalRows = Infinity;
      while (allRows.length < totalRows) {
        const payload = await fetchVerificationCriteria({
          search: debouncedSearch || undefined,
          parameter: parameterFilter || undefined,
          isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
          pageNumber: page,
          pageSize: pageSizeAll,
        });
        const paged = normalizePagedResponse(payload);
        totalRows = Number(paged.total) || 0;
        allRows = allRows.concat(paged.items || []);
        if (!paged.items?.length) break;
        page += 1;
        if (page > 50) break;
      }

      await downloadDataAsExcel({
        data: allRows.map(mapCriteriaRowForExcel),
        fileName: 'arc-welding-verification-criteria',
        sheetName: 'Criteria',
        columns: CRITERIA_EXCEL_COLUMNS.map((key) => ({
          header: key,
          key,
          width: Math.max(14, key.length + 2),
        })),
      });
    } catch (err) {
      notification.error({ message: 'Export failed', description: String(err?.message || err) });
    }
  };

  const handleImport = async (file) => {
    setImporting(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const result = await importVerificationCriteriaXlsx(fileBase64);
      const imported = Number(result?.imported ?? 0);
      if (imported > 0) {
        notification.success({
          message: 'Import complete',
          description: `Imported: ${imported} rule(s)`,
        });
      } else {
        notification.warning({
          message: 'Import finished with 0 rules',
          description:
            result?.message ||
            'No rows were imported. Check the Excel template/columns (or BE validation).',
        });
      }
      load();
    } catch (err) {
      notification.error({ message: 'Import failed', description: String(err?.message || err) });
    } finally {
      setImporting(false);
    }
    return false;
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateVerificationCriterion(editing.id, values);
        notification.success({ message: 'Criterion updated' });
      } else {
        await createVerificationCriterion(values);
        notification.success({ message: 'Criterion created' });
      }
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      notification.error({ message: 'Save failed', description: String(err?.message || err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = useCallback((record) => {
    setDeleteTarget(record);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteVerificationCriterion(deleteTarget.id);
      notification.success({ message: 'Criterion deleted' });
      setDeleteTarget(null);
      load();
    } catch (err) {
      notification.error({ message: 'Delete failed', description: String(err?.message || err) });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, load]);

  const filteredItems = useMemo(() => {
    const entries = Object.entries(columnFilters).filter(([key, v]) => v && !['parameter', 'method', 'grade'].includes(key));
    if (!entries.length) return items;
    return items.filter((row) =>
      entries.every(([key, val]) =>
        String(row[key] ?? '').toLowerCase().includes(String(val).toLowerCase())
      )
    );
  }, [items, columnFilters]);

  const columns = useMemo(
    () => [
      buildTextColumn('parameter', 'Parameter', {
        filterValue: columnFilters.parameter,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, parameter: v })),
      }),
      buildTextColumn('method', 'Method', {
        filterValue: columnFilters.method,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, method: v })),
      }),
      buildTextColumn('grade', 'Grade', {
        filterValue: columnFilters.grade,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, grade: v })),
      }),
      buildTextColumn('rangeCondition', 'Range Condition', { width: 160 }),
      buildTextColumn('toleranceType', 'Tolerance Type', { width: 140 }),
      buildTextColumn('toleranceValue', 'Tolerance Value', { width: 130 }),
      buildTextColumn('toleranceBasis', 'Tolerance Basis', { width: 140 }),
      buildTextColumn('displayOrder', 'Order', { width: 80 }),
      buildTextColumn('isActive', 'Active', {
        width: 80,
        render: (v) => (v === false ? 'No' : 'Yes'),
      }),
      buildActionsColumn({
        onEdit: (record) => {
          setEditing(record);
          setFormOpen(true);
        },
        onDelete: handleDeleteRequest,
        canDelete: (record) => record.isActive !== false,
        deleteDisabledTooltip: 'Make criterion active',
      }),
    ],
    [columnFilters, handleDeleteRequest]
  );

  const filterBar = (
    <>
      <AwFilterGroup label={AW_FILTER_LABELS.search} className="aw-filter-group--search">
        <AwFilterSearch
          placeholder="Search Criteria"
          value={searchInput}
          onChange={setSearchInput}
        />
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.parameter} className="aw-filter-group--select">
        <AwFilterSelect
          placeholder="All Parameters"
          value={parameterFilter}
          onChange={setParameterFilter}
        >
          {options.parameter?.map((p) => (
            <Option key={p} value={p}>{p}</Option>
          ))}
        </AwFilterSelect>
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.status} className="aw-filter-group--select-sm">
        <AwFilterSelect
          placeholder="All Statuses"
          value={isActiveFilter}
          onChange={setIsActiveFilter}
        >
          <Option value="true">Active</Option>
          <Option value="false">Inactive</Option>
        </AwFilterSelect>
      </AwFilterGroup>
    </>
  );

  if (formOpen) {
    return (
      <ArcWeldingCriteriaForm
        open={formOpen}
        initialValues={editing}
        onCancel={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
        loading={saving}
      />
    );
  }

  return (
    <>
      <ArcWeldingListShell
        title="Verification Criteria"
        description="IEC 60974-14 tolerance rules — Tables 1 & 2."
        count={total}
        filterBar={filterBar}
        headerActions={[
          {
            key: 'export',
            label: 'Download As Excel',
            variant: 'tertiary',
            icon: FileSpreadsheet,
            onClick: handleExport,
          },
          {
            key: 'import',
            label: importing ? 'Importing...' : 'Import Excel',
            variant: 'tertiary',
            icon: UploadIcon,
            onClick: () => importInputRef.current?.click(),
            disabled: importing,
          },
          {
            key: 'add',
            label: 'Add New',
            variant: 'primary',
            icon: Plus,
            onClick: () => {
              setEditing(null);
              setFormOpen(true);
            },
          },
        ]}
        columns={columns}
        dataSource={filteredItems}
        rowKey={(r, i) => r.id ?? `${r.parameter}-${r.method}-${i}`}
        loading={loading}
        scroll={{ x: 1600 }}
        pagination={buildServerPagination({
          total,
          pageNumber,
          pageSize,
          onChange: (page, size) => {
            setPageNumber(page);
            setPageSize(size);
          },
        })}
      />

      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImport(file);
          e.target.value = '';
        }}
      />

      <ConfirmationModal
        title="Delete Verification Criterion?"
        message={
          deleteTarget
            ? `Are you sure you want to delete this ${deleteTarget.parameter || ''} ${deleteTarget.method || ''} criterion?`
            : 'Are you sure you want to delete this verification criterion?'
        }
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        isOpen={Boolean(deleteTarget)}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (deleting) return;
          setDeleteTarget(null);
        }}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
          disabled: deleting,
        }}
      />
    </>
  );
};

export default ArcWeldingCriteriaPage;
